(() => {
  if (window.LennysAccessibleVideo) return;

  const playerInstances = new WeakMap();
  const versionInstances = new WeakMap();

  class AccessibleVideoPlayer {
    constructor(player) {
      this.player = player;
      this.video = player.querySelector("video");
      this.controls = player.querySelector("[data-video-controls]");
      this.playButton = player.querySelector("[data-video-play-toggle]");
      this.muteButton = player.querySelector("[data-video-mute]");
      this.transcriptButton = player.querySelector("[data-video-transcript]");
      this.transcriptText = this.transcriptButton?.querySelector("span");
      this.transcriptDetails = this.transcriptButton
        ? document.getElementById(this.transcriptButton.getAttribute("aria-controls"))
        : null;
      this.transcriptHeading = this.transcriptDetails?.querySelector("[data-transcript-heading]");
      this.transcriptDialog = this.transcriptButton
        ? document.getElementById(this.transcriptButton.dataset.dialogId)
        : null;
      this.transcriptDialogHeading = this.transcriptDialog?.querySelector("[data-video-dialog-heading]");
      this.transcriptClose = this.transcriptDialog?.querySelector("[data-video-dialog-close]");
      this.transcriptDialogParent = this.transcriptDialog?.parentElement;
      this.transcriptDialogNextSibling = this.transcriptDialog?.nextSibling;
      this.status = player.querySelector("[data-video-status]");
      this.abortController = new AbortController();
      this.mutationObserver = null;
      this.lastNonZeroVolume = this.video?.volume > 0 ? this.video.volume : 1;

      if (
        !this.video || !this.controls || !this.playButton || !this.muteButton || !this.status ||
        (this.transcriptButton && (
          !this.transcriptDetails?.matches("details.accessible-video__transcript") ||
          this.transcriptDetails.id !== this.transcriptButton.getAttribute("aria-controls") ||
          !this.transcriptHeading || !this.transcriptText
        ))
      ) {
        return;
      }

      try {
        this.bindEvents();
        this.observeCaptionTracks();
        this.syncPlayback();
        this.syncVolume();
        this.syncCaptionTracks();
        this.syncTranscript();
        if (document.readyState === "complete") this.prepareTranscriptDialog();
      } catch (_error) {
        this.abortController.abort();
        this.mutationObserver?.disconnect();
        this.video.controls = true;
        this.controls.hidden = true;
        return;
      }

      this.video.controls = false;
      this.controls.hidden = false;
      this.player.dataset.playerEnhanced = "true";
      this.ready = true;
    }

    get labels() {
      return this.player.dataset;
    }

    bindEvents() {
      const signal = this.abortController.signal;
      const on = (target, eventName, handler) => {
        target.addEventListener(eventName, handler, { signal });
      };

      on(this.playButton, "click", () => this.togglePlayback());
      on(this.muteButton, "click", () => this.toggleMute());
      if (this.transcriptButton) {
        on(this.transcriptButton, "click", () => this.toggleTranscript());
        on(this.transcriptDetails, "toggle", () => {
          if (!this.transcriptDetails.open) this.prepareTranscriptDialog();
          this.syncTranscript();
        });
        if (this.transcriptDialog) {
          on(this.transcriptClose, "click", () => window.closeDialog?.(this.transcriptClose));
          on(document, "DOMContentLoaded", () => this.prepareTranscriptDialog());
        }
        on(this.transcriptDetails, "keydown", (event) => {
          if (event.key === "Escape" && this.transcriptDetails.open) {
            this.transcriptDetails.open = false;
            this.syncTranscript();
            this.transcriptButton.focus();
          }
        });
      }
      on(this.video, "play", () => this.syncPlayback());
      on(this.video, "pause", () => this.syncPlayback());
      on(this.video, "ended", () => this.syncPlayback());
      on(this.video, "volumechange", () => this.syncVolume());
      on(this.video, "loadedmetadata", () => this.syncCaptionTracks());

      if (this.video.textTracks?.addEventListener) {
        on(this.video.textTracks, "addtrack", () => this.syncCaptionTracks());
      }
    }

    observeCaptionTracks() {
      if (!window.MutationObserver) return;

      this.mutationObserver = new MutationObserver(() => this.syncCaptionTracks());
      this.mutationObserver.observe(this.video, { childList: true });
    }

    async togglePlayback() {
      if (!this.video.paused && !this.video.ended) {
        this.video.pause();
        return;
      }

      this.status.textContent = "";

      try {
        await this.video.play();
      } catch (_error) {
        this.status.textContent = this.labels.playError;
        this.syncPlayback();
      }
    }

    syncPlayback() {
      const isPlaying = !this.video.paused && !this.video.ended;
      this.player.dataset.playerPlaying = String(isPlaying);
      this.playButton.setAttribute(
        "aria-label",
        isPlaying ? this.labels.labelPause : this.labels.labelPlay,
      );
    }

    toggleMute() {
      const isMuted = this.video.muted || this.video.volume === 0;

      if (isMuted) {
        if (this.video.volume === 0) this.video.volume = this.lastNonZeroVolume || 1;
        this.video.muted = false;
      } else {
        this.video.muted = true;
      }

      this.syncVolume();
    }

    syncVolume() {
      const isMuted = this.video.muted || this.video.volume === 0;
      if (!isMuted) this.lastNonZeroVolume = this.video.volume;

      this.player.dataset.playerMuted = String(isMuted);
      this.muteButton.setAttribute(
        "aria-label",
        isMuted ? this.labels.labelUnmute : this.labels.labelMute,
      );
    }

    syncCaptionTracks() {
      this.video.querySelectorAll('track[kind="captions"]').forEach((track) => {
        if (track.default && track.track) track.track.mode = "showing";
      });
    }

    prepareTranscriptDialog() {
      if (
        !this.transcriptDialog?.matches('[role="dialog"][aria-modal="true"]') ||
        !this.transcriptDialogHeading || !this.transcriptClose ||
        !window.openDialog || !window.closeDialog || this.transcriptDetails.open
      ) return;

      this.transcriptDetails.hidden = true;
      this.transcriptButton.setAttribute("aria-controls", this.transcriptDialog.id);
      this.transcriptButton.setAttribute("aria-haspopup", "dialog");
      this.syncTranscript();
    }

    toggleTranscript() {
      this.prepareTranscriptDialog();
      if (this.transcriptDetails.hidden) {
        this.video.pause();
        try {
          // Keep the shared theme dialog's backdrop out of the video/hero stacking context.
          if (this.transcriptDialog.parentElement === this.transcriptDialogParent) {
            document.body.appendChild(this.transcriptDialog);
          }
          window.openDialog(this.transcriptDialog.id, this.transcriptButton, this.transcriptDialogHeading);
          this.transcriptDialog.parentElement.classList.add("accessible-video__dialog-backdrop");
          return;
        } catch (_error) {
          this.restoreTranscriptDialog();
          this.transcriptDetails.hidden = false;
          this.transcriptButton.setAttribute("aria-controls", this.transcriptDetails.id);
          this.transcriptButton.removeAttribute("aria-haspopup");
        }
      }
      this.transcriptDetails.open = !this.transcriptDetails.open;
      this.syncTranscript();
      if (this.transcriptDetails.open) this.transcriptHeading.focus();
    }

    syncTranscript() {
      if (this.transcriptButton) {
        const label = this.transcriptDetails.open
          ? this.transcriptButton.dataset.labelHide
          : this.transcriptButton.dataset.labelShow;
        if (this.transcriptDetails.hidden) {
          this.transcriptButton.removeAttribute("aria-expanded");
        } else {
          this.transcriptButton.setAttribute("aria-expanded", String(this.transcriptDetails.open));
        }
        this.transcriptButton.setAttribute("aria-label", label);
        this.transcriptText.textContent = label;
      }
    }

    restoreTranscriptDialog() {
      if (!this.transcriptDialogParent || this.transcriptDialog.parentElement === this.transcriptDialogParent) return;
      const backdrop = this.transcriptDialog.parentElement;
      const nextSibling = this.transcriptDialogNextSibling?.parentNode === this.transcriptDialogParent
        ? this.transcriptDialogNextSibling
        : null;
      this.transcriptDialogParent.insertBefore(this.transcriptDialog, nextSibling);
      if (backdrop.classList.contains("dialog-backdrop")) backdrop.remove();
    }

    destroy() {
      if (window.aria?.getCurrentDialog?.()?.dialogNode === this.transcriptDialog) window.aria.closeCurrentDialog();
      this.restoreTranscriptDialog();
      this.abortController.abort();
      this.mutationObserver?.disconnect();
      if (this.transcriptDetails) {
        this.transcriptDetails.hidden = false;
        this.transcriptButton.setAttribute("aria-controls", this.transcriptDetails.id);
        this.transcriptButton.removeAttribute("aria-haspopup");
      }
      this.video.controls = true;
      this.controls.hidden = true;
      delete this.player.dataset.playerEnhanced;
      delete this.player.dataset.playerPlaying;
      delete this.player.dataset.playerMuted;
    }
  }

  class AccessibleVideoVersions {
    constructor(container) {
      this.container = container;
      this.standard = container.querySelector('[data-video-version="standard"]');
      this.described = container.querySelector('[data-video-version="described"]');
      this.switchButton = container.querySelector("[data-video-version-switch]");
      this.switchLabel = this.switchButton?.querySelector("[data-video-version-switch-label]");
      this.status = container.querySelector("[data-video-version-status]");
      this.abortController = new AbortController();
      this.standardPlayer = playerInstances.get(this.standard?.querySelector("[data-accessible-video-player]"));
      this.describedPlayer = playerInstances.get(this.described?.querySelector("[data-accessible-video-player]"));

      // Without a usable pair, keep both versions accessible with native controls.
      if (!this.standardPlayer || !this.describedPlayer || !this.switchButton || !this.switchLabel || !this.status) {
        for (const wrapper of [this.standard, this.described]) {
          const player = wrapper?.querySelector("[data-accessible-video-player]");
          playerInstances.get(player)?.destroy();
          if (player) playerInstances.delete(player);
        }
        return;
      }

      try {
        const signal = this.abortController.signal;
        this.switchButton.addEventListener("click", () => this.toggle(), { signal });
        this.standardPlayer.video.addEventListener("play", () => this.describedPlayer.video.pause(), { signal });
        this.describedPlayer.video.addEventListener("play", () => this.standardPlayer.video.pause(), { signal });
        this.described.hidden = true;
        this.container.dataset.versionEnhanced = "true";
        this.switchButton.hidden = false;
        this.ready = true;
      } catch (_error) {
        this.abortController.abort();
        this.described.hidden = false;
        this.switchButton.hidden = true;
        delete this.container.dataset.versionEnhanced;
      }
    }

    toggle() {
      const describedActive = !this.described.hidden;
      const leaving = describedActive ? this.describedPlayer : this.standardPlayer;
      const entering = describedActive ? this.standardPlayer : this.describedPlayer;
      leaving.video.pause();
      entering.video.pause();
      // The timelines differ because description pauses the picture. Restart rather
      // than seeking to the same timestamp and unexpectedly skipping dialogue.
      for (const video of [leaving.video, entering.video]) {
        try { video.currentTime = 0; } catch (_error) { /* Metadata may not have loaded yet. */ }
      }
      this.standard.hidden = !describedActive;
      this.described.hidden = describedActive;
      this.switchLabel.textContent = describedActive
        ? this.switchButton.dataset.labelEnable
        : this.switchButton.dataset.labelDisable;
      this.status.textContent = describedActive
        ? "Standard video selected. Play from the beginning."
        : "Audio-described video selected. Play from the beginning.";
    }

    destroy() {
      this.abortController.abort();
      this.standardPlayer.video.pause();
      this.describedPlayer.video.pause();
      this.standard.hidden = false;
      this.described.hidden = false;
      this.switchButton.hidden = true;
      this.switchLabel.textContent = this.switchButton.dataset.labelEnable;
      this.status.textContent = "";
      delete this.container.dataset.versionEnhanced;
    }
  }

  const getVersionGroups = (root) => {
    if (root.matches?.("[data-video-versions]")) return [root];
    return Array.from(root.querySelectorAll?.("[data-video-versions]") || []);
  };

  const initializeVersionGroups = (root) => {
    getVersionGroups(root).forEach((container) => {
      if (versionInstances.has(container)) return;
      const instance = new AccessibleVideoVersions(container);
      if (instance.ready) versionInstances.set(container, instance);
    });
  };

  const destroyVersionGroups = (root) => {
    getVersionGroups(root).forEach((container) => {
      versionInstances.get(container)?.destroy();
      versionInstances.delete(container);
    });
  };

  const getPlayers = (root) => {
    if (root.matches?.("[data-accessible-video-player]")) return [root];
    return Array.from(root.querySelectorAll?.("[data-accessible-video-player]") || []);
  };

  const initializePlayers = (root = document) => {
    getPlayers(root).forEach((player) => {
      if (playerInstances.has(player)) return;

      const instance = new AccessibleVideoPlayer(player);
      if (instance.ready) playerInstances.set(player, instance);
    });
    initializeVersionGroups(root);
  };

  const destroyPlayers = (root) => {
    destroyVersionGroups(root);
    getPlayers(root).forEach((player) => {
      playerInstances.get(player)?.destroy();
      playerInstances.delete(player);
    });
  };

  document.addEventListener("shopify:section:load", (event) => initializePlayers(event.target));
  document.addEventListener("shopify:section:unload", (event) => destroyPlayers(event.target));

  window.LennysAccessibleVideo = { initializePlayers };
  initializePlayers();
})();
